// src/components/AddEvent.jsx
import { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { v4 as uuidv4 } from 'uuid';
import { getRepeatedEvents } from '../utils/eventRepeatUtils';
import { doc, setDoc, collection, query, orderBy, getDocs, Timestamp } from "firebase/firestore";
import { db, firestoreAsatizah } from '../utils/FirebaseConfig';
import { getStorage, ref, uploadString, getDownloadURL } from 'firebase/storage';
import '../styles/AddEvent.css';
import { toYMD, buildSeriesKey } from "../utils/recurringUtils";
import { FiCheckCircle } from "react-icons/fi";
import { saveEvent } from "../services/mosqueContentRepository";


const storage = getStorage();
const MAX_IMAGE_SIZE = 1024 * 1024; // 1MB in bytes

const kuliahPresets = [
  { title: "Kuliah Subuh", time: "06:30" },
  { title: "Kuliah Dhuha", time: "10:00" },
  { title: "Tazkirah Jumaat", time: "12:30" },
  { title: "Kuliah Zohor", time: "13:45" },
  { title: "Kuliah Maghrib", time: "19:30" },
];

const programPresets = [
  { title: "Bacaan Yasin", time: "20:00", posterUrl: "/images/yasin.png" },
];

const weekOfMonth = (d) => Math.floor((d.getDate() - 1) / 7) + 1;

const isLastSameDowInMonth = (d) => {
  const x = new Date(d);
  x.setDate(x.getDate() + 7);
  return x.getMonth() !== d.getMonth();
};

const AddEvent = ({
  asatizahData = [],
  startTime,
  setStartTime,
  selectedDate,
  eventTitle,
  speakerName,
  speakerImage,
  speakerPosition,
  setEventTitle,
  setSpeakerName,
  setSpeakerImage,
  setSpeakerPosition,
  closeModal,
  setPosterImage,
  posterImage,
  refreshEvents,
  eventId,
  selectedEvent,
  onCopyAsNew,
  pendingException,
  commitPendingException,
}) => {
  const [repeat, setRepeat] = useState('none');
  const [repeatEndDate, setRepeatEndDate] = useState(null);
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);
  const [isKuliahChecked, setIsKuliahChecked] = useState(true);
  const [isPosterToEtv, setIsPosterToEtv] = useState(false);
  const [subjek, setSubjek] = useState('Umum');
  const [tajuk, setTajuk] = useState('');
  const [isFormValid, setIsFormValid] = useState(false);
  const [kitabList, setKitabList] = useState([]);
  const [selectedKitab, setSelectedKitab] = useState(null);
  const [nota, setNota] = useState("");
  const [kitabMode, setKitabMode] = useState("kitab");

  // ✅ editing flags
  const isEditingSeries = !!eventId && !!selectedEvent?.extendedProps?.isRecurring;
  const isEditingSingle = !!eventId && !selectedEvent?.extendedProps?.isRecurring;
  const isEditing = isEditingSingle || isEditingSeries;

  // ✅ COPY dialog state
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyDate, setCopyDate] = useState(""); // yyyy-mm-dd

  const isSelfSpeakerId = (uid) => {
    if (!uid) return false;

    // kalau Calendar.jsx dah merge & letak source
    const found = (asatizahData || []).some(a => {
      const src = a?.source || (a?.uid ? "self" : "admin");
      if (src !== "self") return false;
      return a?.uid === uid || a?.id === uid;
    });

    return found;
  };


  const speakerOptions = useMemo(() => {
    return (asatizahData || [])
      .filter(p => (p?.nama || "").trim())
      .slice()
      .sort((a, b) =>
        String(a.nama).toLowerCase().localeCompare(String(b.nama).toLowerCase())
      )
      .map(p => {
        const source = p.source || (p.uid ? "self" : "admin");
        const uid = source === "self" ? (p.uid || p.id) : null;

        return {
          value: p.nama || "",
          label: p.nama || "",
          source,         // "admin" | "self"
          uid,            // null kalau admin
          image: p.image || p.gambar || p.photoURL || "",
          position: p.jawatan || "",
          raw: p,
        };
      });
  }, [asatizahData]);




  useEffect(() => {
    let alive = true;

    (async () => {
      const cached = sessionStorage.getItem("kitabs_cache_v1");
      if (cached) {
        if (alive) setKitabList(JSON.parse(cached));
        return;
      }

      const q = query(
        collection(db, "kitabs"),
        orderBy("normalizedTitle", "asc"),
        // kalau kitab banyak sangat, letak limit dan buat search
        // limit(300)
      );
      const snap = await getDocs(q);
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      if (!alive) return;
      setKitabList(rows);
      sessionStorage.setItem("kitabs_cache_v1", JSON.stringify(rows));
    })();

    return () => { alive = false; };
  }, []);




  const kitabOptions = useMemo(() => (
    kitabList.map(k => ({
      value: k.id,
      label: k.title || "",
      data: k,
      coverImage: k.coverImage || "",
      meta: {
        category: k.category || "Umum",
        author: k.author || ""
      }
    }))
  ), [kitabList]);

  const handleKitabSelect = (opt) => {
    setSelectedKitab(opt);

    const k = opt?.data;
    if (k) {
      setTajuk(k.title || "");
      setSubjek(k.category || "Umum");
    } else {
      setTajuk("");
      setSubjek("Umum");
    }
  };



  const handlePoster = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Image size exceeds 1MB. Please upload an image smaller than 1MB.');
      event.target.value = '';
      return;
    }
    reader.onload = (e) => {
      const image = e.target.result;
      setPosterImage(image);
    };
    reader.readAsDataURL(file);
  };

  const uploadImage = async (imageData, imagePath) => {
    const storageRef = ref(storage, imagePath);
    if (!imageData.startsWith('data:')) throw new Error('Invalid image data URL');
    await uploadString(storageRef, imageData, 'data_url');
    return await getDownloadURL(storageRef);
  };

  const stripDataUrl = (v) => {
    const s = String(v || "");
    return s.startsWith("data:image/") ? "" : s;
  };


  const buildStartDate = (baseDate, timeStr) => {
    const [hh, mm] = (timeStr || "00:00").split(":").map(Number);
    const d = new Date(baseDate);
    d.setHours(hh || 0, mm || 0, 0, 0);
    return d;
  };

  const formatDate = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // ✅ COPY (Edit -> Add new with prefill)
  const openCopyDialog = () => {
    const base = new Date(selectedDate || new Date());
    const d = new Date(base);
    d.setMonth(d.getMonth() + 1);
    setCopyDate(formatDate(d));
    setCopyOpen(true);
  };

  const runCopyAsNew = () => {
    if (!copyDate) return;

    if (typeof onCopyAsNew !== "function") {
      toast.error("onCopyAsNew belum disambungkan di Calendar.jsx");
      return;
    }

    // ✅ tukar ke Add Mode di parent
    onCopyAsNew({ targetYMD: copyDate });

    // ✅ lepas switch mode, kita biar form fields kekal (prefill)
    // repeat dropdown akan muncul sebab parent set eventId=null & selectedEvent=null
    setCopyOpen(false);

    // ✅ default repeat reset (optional, tapi selamat)
    setRepeat("none");
    setRepeatEndDate(null);
  };

  const handleAddOrUpdateEvent = async () => {
    const userProfile = JSON.parse(localStorage.getItem("userProfile") || "{}");
    const mosqueId = userProfile?.mosqueId || null;

    if (!mosqueId) {
      toast.error("mosqueId tidak dijumpai. Sila logout/login semula.");
      return;
    }
    if (!selectedDate) {
      toast.error("Tarikh tidak sah.");
      return;
    }

    const eventField = isKuliahChecked ? 'kuliah' : 'program';
    let eventsToAdd = [];

    // Upload poster image hanya jika program
    let posterFinal = posterImage || "";
    if (!isKuliahChecked && posterFinal && String(posterFinal).startsWith("data:")) {
      const posterImagePath = `${mosqueId}/posterImages/${Date.now()}.jpg`;
      posterFinal = await uploadImage(posterFinal, posterImagePath);
    }

    const kitabIdFinal =
      (isKuliahChecked && kitabMode === "kitab")
        ? (selectedKitab?.value || null)
        : null;

    const kitabMeta =
      (isKuliahChecked && kitabMode === "kitab" && selectedKitab?.data)
        ? {
          kitabId: selectedKitab.value,
          kitabTitle: selectedKitab.data.title || "",
          kitabCoverImage: selectedKitab.data.coverImage || "",
          kitabCategory: selectedKitab.data.category || "Umum",
          kitabAuthor: selectedKitab.data.author || "",
        }
        : {
          kitabId: kitabIdFinal,
          kitabTitle: "",
          kitabCoverImage: "",
          kitabCategory: "",
          kitabAuthor: "",
        };

    const recurringField = isKuliahChecked ? "kuliahRecurring" : "programRecurring";
    const isRuleRepeat = (repeat === "weekly" || repeat === "monthly") && repeatEndDate;

    // ==========================
    // ✅ RULE-BASED RECURRING (series)
    // ==========================
    if (isRuleRepeat) {
      const titleFinal = eventTitle?.trim() || "";
      const timeFinal = startTime || "00:00";

      const repeatStart = isEditingSeries
        ? (selectedEvent?.extendedProps?.repeatStart || toYMD(new Date(selectedDate)))
        : toYMD(new Date(selectedDate));

      const repeatEnd = toYMD(new Date(repeatEndDate));
      const seriesIdFinal = eventId || uuidv4(); // ✅ if add-new, eventId null => new series id

      const ruleBase = {
        seriesId: seriesIdFinal,
        title: titleFinal,
        time: timeFinal,
        repeat,
        repeatStart,
        repeatEnd,

        // ✅ WAJIB: untuk ETV query where("start", ">=", ...)
        start: Timestamp.fromDate(buildStartDate(new Date(selectedDate), timeFinal)),
      };


      let rule;
      if (repeat === "weekly") {
        const dow = isEditingSeries
          ? Number(selectedEvent?.extendedProps?.repeatDow ?? selectedDate.getDay())
          : selectedDate.getDay();

        rule = {
          ...ruleBase,
          repeatDow: dow,
          seriesKey: buildSeriesKey({ title: titleFinal, dow, time: timeFinal }),
        };
      } else {
        const base = new Date(selectedDate);
        base.setHours(0, 0, 0, 0);

        const dow = isEditingSeries
          ? Number(selectedEvent?.extendedProps?.repeatDow ?? base.getDay())
          : base.getDay();

        const nth = isEditingSeries
          ? Number(
            selectedEvent?.extendedProps?.repeatNth ??
            (isLastSameDowInMonth(base) ? -1 : weekOfMonth(base))
          )
          : (isLastSameDowInMonth(base) ? -1 : weekOfMonth(base));

        rule = {
          ...ruleBase,
          repeatMode: "nthDow",
          repeatDow: dow,
          repeatNth: nth,
          seriesKey: `m|${titleFinal}|${dow}|${nth}|${timeFinal}`.toLowerCase(),
        };
      }

      const speakerIdResolved = (() => {
        const picked = selectedSpeaker?.uid || null;
        if (isSelfSpeakerId(picked)) return picked;

        const legacy = selectedEvent?.extendedProps?.speakerId || null;
        if (isSelfSpeakerId(legacy)) return legacy;

        return null; // ✅ admin / nama-only => jangan simpan speakerId
      })();


      const speakerNameResolved =
        speakerName ||
        selectedEvent?.extendedProps?.speaker ||
        "";

      const speakerImageResolved =
        stripDataUrl(speakerImage) ||
        stripDataUrl(selectedEvent?.extendedProps?.speakerImage) ||
        "";


      const speakerPositionResolved =
        speakerPosition ||
        selectedEvent?.extendedProps?.speakerPosition ||
        "Penceramah";

      if (isKuliahChecked) {
        rule = {
          ...rule,
          isKuliah: true,
          speakerId: speakerIdResolved,
          speaker: speakerNameResolved,
          speakerImage: speakerImageResolved,
          speakerPosition: speakerPositionResolved,
          subjek,
          tajuk,
          nota: nota || "",
          kitabId: kitabIdFinal,
          ...kitabMeta,
        };
      } else {
        rule = {
          ...rule,
          isKuliah: false,
          posterImage: posterFinal || "",
          isPosterToEtv: !!isPosterToEtv,
        };
      }

      try {
        await saveEvent(
          mosqueId,
          {
            ...rule,
            eventId: rule.seriesId,
            mosqueId,
            updatedAt: Date.now(),
            createdAt: selectedEvent?.extendedProps?.createdAt || Date.now(),
            exDates: Array.isArray(rule.exDates) ? rule.exDates : [],
          },
          { legacyCollection: recurringField }
        );

        if (rule.isKuliah && rule.speakerId && isSelfSpeakerId(rule.speakerId)) {
          await syncSeriesToAsatizah(rule, rule.speakerId, mosqueId);
        }


        toast.success(`Series ${repeat} berjaya disimpan!`);
        if (pendingException && typeof commitPendingException === "function") {
          await commitPendingException();
        }
        refreshEvents();
        closeModal();
        return;
      } catch (e) {
        console.error("❌ save series error:", e);
        toast.error(e?.code ? `Gagal simpan series: ${e.code}` : "Gagal simpan series.");
        return;
      }
    }

    // ==========================
    // ✅ LEGACY GENERATE EVENTS (optional)
    // ==========================
    if (repeat !== 'none' && repeatEndDate) {
      eventsToAdd = getRepeatedEvents(
        repeat,
        new Date(selectedDate),
        startTime,
        eventTitle,
        speakerName,
        stripDataUrl(speakerImage),  // ✅ jangan simpan dataURL
        posterFinal,                 // ✅ guna yang dah upload kalau program
        speakerPosition,
        subjek,
        tajuk,
        repeatEndDate,
        isKuliahChecked,
        isPosterToEtv,
        nota,
        kitabIdFinal
      );

    } else {
      let eventToAdd;

      if (isKuliahChecked) {
        eventToAdd = {
          title: eventTitle,
          speaker: speakerName,
          speakerId: isSelfSpeakerId(selectedSpeaker?.uid) ? selectedSpeaker.uid : null,
          speakerImage: stripDataUrl(speakerImage) || "",
          speakerPosition: speakerPosition || "Penceramah",
          start: buildStartDate(selectedDate, startTime),
          allDay: false,
          isKuliah: true,
          subjek,
          tajuk,
          nota: nota || "",
          kitabId: kitabIdFinal,
          ...kitabMeta,
          time: startTime,
          eventId: eventId || uuidv4(), // ✅ if copy-add mode, eventId null => new id
        };
      } else {
        eventToAdd = {
          title: eventTitle,
          posterImage: posterFinal,
          start: buildStartDate(selectedDate, startTime),
          allDay: false,
          isKuliah: false,
          eventId: eventId || uuidv4(),
          isPosterToEtv,
          time: startTime,
        };
      }

      eventsToAdd.push(eventToAdd);
      console.log("📝 Single event created:", eventToAdd);
    }

    // Inject kitab meta for all kuliah items
    if (isKuliahChecked) {
      eventsToAdd = eventsToAdd.map(ev => ({ ...ev, ...kitabMeta }));
    }

    eventsToAdd = eventsToAdd.map((ev) => ({
      ...ev,
      speakerImage: stripDataUrl(ev.speakerImage),
      posterImage: stripDataUrl(ev.posterImage),
    }));


    try {
      const ev = eventsToAdd[0]; // single save
      const evId = ev.eventId;
      await saveEvent(mosqueId, {
        ...ev,
        eventId: evId,
        mosqueId: mosqueId || "",
        updatedAt: Date.now(),
        createdAt: ev.createdAt || Date.now(),
      }, { legacyCollection: eventField });

      console.log("🔥 [AddEvent] Saved:", eventField, evId);

      if (isKuliahChecked) {
        const sid = selectedSpeaker?.uid || ev.speakerId || null;
        if (sid && isSelfSpeakerId(sid)) {
          await syncEventToAsatizah(ev, sid, mosqueId);
        }
      }

      if (pendingException && typeof commitPendingException === "function") {
        await commitPendingException();
      }
      refreshEvents();
    } catch (error) {
      console.error("Error adding document: ", error);
      toast.error('Failed to add event.');
      return;
    }

    closeModal();
  };

  const handleKuliahCheckboxChange = () => {
    setIsKuliahChecked(!isKuliahChecked);
    setSelectedSpeaker(null);
  };

  const handlePosterToEtvChange = () => {
    setIsPosterToEtv(!isPosterToEtv);
  };

  const handleRepeatEndDateChange = (e) => {
    const [y, m, d] = e.target.value.split("-").map(Number);
    setRepeatEndDate(new Date(y, (m || 1) - 1, d || 1));
  };

  const repeatOptions = [
    { value: 'none', label: 'Tidak Berulang' },
    { value: 'weekly', label: 'Ulang Mingguan' },
    { value: 'monthly', label: 'Ulang Bulanan' },
  ];

  useEffect(() => {
    const isPrefillNew = !eventId;

    // ===== repeat prefill (edit recurring sahaja) =====
    if (!isPrefillNew && selectedEvent?.extendedProps?.isRecurring) {
      setRepeat(selectedEvent.extendedProps.repeat || "weekly");

      const parseYMDLocal = (ymd) => {
        const [y, m, d] = String(ymd || "").split("-").map(Number);
        if (!y) return null;
        return new Date(y, (m || 1) - 1, d || 1);
      };

      const end = selectedEvent.extendedProps.repeatEnd;
      setRepeatEndDate(end ? parseYMDLocal(end) : null);
    } else {
      setRepeat("none");
      setRepeatEndDate(null);
    }

    if (!selectedEvent) return;

    const isKuliah = selectedEvent?.extendedProps?.isKuliah !== false;
    setIsKuliahChecked(isKuliah);

    // ===== common prefill =====
    const time = selectedEvent.extendedProps?.time;
    if (time) setStartTime(time);

    const title = selectedEvent.title || "";
    setEventTitle(title);

    if (isKuliah) {
      // kitab mode
      const kitabId = selectedEvent.extendedProps?.kitabId || null;
      setKitabMode(kitabId ? "kitab" : "manual");

      setNota(selectedEvent.extendedProps?.nota || "");

      // ===== kitab prefill (guna kitabOptions, bukan kitabList) =====
      if (kitabId) {
        const optK = kitabOptions.find((o) => o.value === kitabId) || null;
        setSelectedKitab(optK);

        setTajuk(
          optK?.data?.title ||
          selectedEvent.extendedProps?.tajuk ||
          ""
        );
        setSubjek(
          optK?.data?.category ||
          selectedEvent.extendedProps?.subjek ||
          "Umum"
        );
      } else {
        setSelectedKitab(null);
        setTajuk(selectedEvent.extendedProps?.tajuk || "");
        setSubjek(selectedEvent.extendedProps?.subjek || "Umum");
      }

      // ===== speaker prefill (guna speakerOptions + fallback) =====
      const ustazName = selectedEvent.extendedProps?.speaker || "";
      const sid = selectedEvent.extendedProps?.speakerId || null;

      // ✅ priority: match by speakerId (self), then by name (self first), then any
      const optById = sid ? speakerOptions.find(o => o.uid === sid) : null;
      const optByNameSelf = speakerOptions.find(o => o.value === ustazName && o.source === "self") || null;
      const optByNameAny = speakerOptions.find(o => o.value === ustazName) || null;

      const optS = optById || optByNameSelf || optByNameAny;

      setSelectedSpeaker(optS || null);
      setSpeakerName(optS?.value || ustazName || "");
      setSpeakerImage(optS?.image || selectedEvent.extendedProps?.speakerImage || "");
      setSpeakerPosition(optS?.position || selectedEvent.extendedProps?.speakerPosition || "");

    } else {
      // program poster
      setPosterImage(selectedEvent.extendedProps?.posterImage || "");
      setIsPosterToEtv(!!selectedEvent.extendedProps?.isPosterToEtv);
    }
  }, [
    eventId,
    selectedEvent,
    speakerOptions,
    kitabOptions,
    setEventTitle,
    setPosterImage,
    setSpeakerImage,
    setSpeakerName,
    setSpeakerPosition,
    setStartTime,
  ]);


  const kitab = [
    { value: 'Umum', label: 'Umum' },
    { value: 'Fekah', label: 'Fekah' },
    { value: 'Tauhid', label: 'Tauhid' },
    { value: 'Sirah', label: 'Sirah' },
    { value: 'Hadis', label: 'Hadis' },
    { value: 'Tafsir', label: 'Tafsir' },
    { value: 'Tajwid', label: 'Tajwid' },
    { value: 'Tasauf', label: 'Tasauf' },
  ];

  useEffect(() => {
    const checkFormValidity = () => {
      if (isKuliahChecked) {
        if (kitabMode === "kitab") {
          return (
            eventTitle.trim() !== "" &&
            selectedSpeaker !== null &&
            startTime !== "" &&
            selectedKitab !== null
          );
        }

        return (
          eventTitle.trim() !== "" &&
          selectedSpeaker !== null &&
          startTime !== "" &&
          subjek !== "" &&
          tajuk.trim() !== ""
        );
      }

      return eventTitle.trim() !== "" && !!posterImage;
    };

    setIsFormValid(checkFormValidity());
  }, [isKuliahChecked, kitabMode, selectedKitab, eventTitle, selectedSpeaker, startTime, subjek, tajuk, posterImage, repeat]);

  const syncEventToAsatizah = async (event, speakerId, mosqueId) => {
    try {
      if (!speakerId) return;
      if (!isSelfSpeakerId(speakerId)) return; // ✅ block admin

      await setDoc(
        doc(firestoreAsatizah, `asatizah/${speakerId}/events/${event.eventId}`),
        { ...event, mosqueId: mosqueId || "" }
      );
      console.log("✅ Event sync ke ustaz:", speakerId, "| mosqueId:", mosqueId);
    } catch (err) {
      console.error("❌ Gagal sync event:", err);
    }
  };

  const syncSeriesToAsatizah = async (rule, speakerId, mosqueId) => {
    try {
      if (!speakerId) return;
      if (!isSelfSpeakerId(speakerId)) return; // ✅ block admin

      await setDoc(
        doc(firestoreAsatizah, `asatizah/${speakerId}/recurring/${rule.seriesId}`),
        { ...rule, mosqueId: mosqueId || "", updatedAt: Date.now() },
        { merge: true }
      );
      console.log("✅ Series sync ke Asatizah:", speakerId, rule.seriesId);
    } catch (err) {
      console.error("❌ Gagal sync series:", err);
    }
  };


  return (
    <div className="container">
      <p className="header-text">
        {isEditing
          ? `Edit Aktiviti ${selectedEvent?.extendedProps?.isKuliah ? 'Kuliah' : 'Poster'}`
          : 'Tambah Aktiviti'}
      </p>

      <h2 className="title-text">
        {selectedDate.toLocaleString('ms-my', { weekday: 'long' })}, {selectedDate.toLocaleDateString('ms-my', { day: 'numeric', month: 'long', year: 'numeric' })}
      </h2>

      {!isEditing && (
        <label className="label">
          <input
            type="checkbox"
            className="input-checkbox"
            checked={isKuliahChecked}
            onChange={handleKuliahCheckboxChange}
          />
          <span style={{ marginLeft: '5px' }}>Kuliah</span>
        </label>
      )}

      {isKuliahChecked && (
        <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 8, marginBottom: 6 }}>
          {kuliahPresets.map((p) => (
            <button
              key={p.title}
              type="button"
              className="filter-button"
              onClick={() => {
                setEventTitle(p.title);
                setStartTime(p.time);
              }}
              style={{ whiteSpace: "nowrap" }}
              title={`${p.title} (${p.time})`}
            >
              {p.title}
            </button>
          ))}
        </div>
      )}

      {!isKuliahChecked && (
        <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 8, marginBottom: 6 }}>
          {programPresets.map((p) => (
            <button
              key={p.title}
              type="button"
              className="filter-button"
              onClick={() => {
                setEventTitle(p.title);
                setStartTime(p.time);
                setPosterImage(p.posterUrl);
              }}
              style={{ whiteSpace: "nowrap" }}
              title={`${p.title} (${p.time})`}
            >
              {p.title}
            </button>
          ))}
        </div>
      )}

      <input
        type="text"
        className="input-text"
        value={eventTitle}
        onChange={(e) => setEventTitle(e.target.value)}
        placeholder={isKuliahChecked ? "Nama Kuliah" : "Nama Program"}
      />

      {isKuliahChecked ? (
        <div style={{ display: "flex" }}>
          <div style={{ flex: "9" }}>
            <Select
              value={selectedSpeaker}
              options={speakerOptions}
              isClearable
              onChange={(opt) => {
                setSelectedSpeaker(opt || null);
                setSpeakerName(opt?.value || "");
                setSpeakerImage(opt?.image || "");
                setSpeakerPosition(opt?.position || "");
              }}
              formatOptionLabel={(opt, { context }) => {
                const RightIcon = opt.source === "self" ? (
                  <FiCheckCircle
                    title="Ustaz ini ada akaun sendiri"
                    style={{ marginLeft: "auto", opacity: 0.9 }}
                  />
                ) : null;


                // bila value (selected)
                if (context === "value") {
                  return (
                    <div className="speaker-container" style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
                      {opt.image ? (
                        <img src={opt.image} alt="" className="speaker-image" />
                      ) : (
                        <div style={{ width: 26, height: 26, borderRadius: "50%", background: "#ddd" }} />
                      )}
                      <span className="speaker-name">{opt.label}</span>
                      {RightIcon}
                    </div>
                  );
                }

                // bila dalam menu dropdown
                return (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
                    <span>{opt.label}</span>
                    {RightIcon}
                  </div>
                );
              }}

            />

          </div>
          <div style={{ flex: "2", margin: "0 0 0 10px" }}>
            <input
              type="time"
              className="input-time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              style={{ width: "100%" }}
            />
          </div>
        </div>
      ) : (
        <div>
          <div style={{ display: "flex" }}>
            <input
              id="poster-upload"
              type="file"
              className="input-file"
              accept="image/*"
              onChange={handlePoster}
            />
            {posterImage && <img src={posterImage} alt="Poster" className="image-preview" />}
            <div style={{ flex: "2", margin: "0 0 0 10px" }}>
              <input
                type="time"
                className="input-time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                style={{ width: "100%" }}
              />
            </div>
          </div>

          {!isEditing && (
            <label className="label">
              <input
                type="checkbox"
                className="input-checkbox"
                checked={isPosterToEtv}
                onChange={handlePosterToEtvChange}
              />
              Masukkan poster ini ke ETV
            </label>
          )}
        </div>
      )}

      {isKuliahChecked && (
        <div>
          <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
            <button
              type="button"
              className={`filter-button ${kitabMode === "kitab" ? "active" : ""}`}
              onClick={() => setKitabMode("kitab")}
            >
              Pilih Kitab
            </button>
            <button
              type="button"
              className={`filter-button ${kitabMode === "manual" ? "active" : ""}`}
              onClick={() => setKitabMode("manual")}
            >
              Cara Lama
            </button>
          </div>

          {kitabMode === "kitab" ? (
            <div style={{ display: "flex" }}>
              <div style={{ flex: "6" }}>
                <Select
                  value={selectedKitab}
                  onChange={handleKitabSelect}
                  options={kitabOptions}
                  isClearable
                  placeholder="Pilih Kitab"
                  className="custom-select"
                  styles={{
                    menu: (p) => ({ ...p, zIndex: 9999 }),
                    control: (p) => ({ ...p, height: "43px" }),
                  }}
                />
              </div>

              <div style={{ flex: "6", margin: "0 0 0 10px" }}>
                <input
                  type="text"
                  className="input-text"
                  value={nota}
                  onChange={(e) => setNota(e.target.value)}
                  placeholder="Topik / Bab (akan masuk nota)"
                  style={{ width: "100%" }}
                />
              </div>
            </div>
          ) : (
            <div style={{ display: "flex" }}>
              <div style={{ flex: "3" }}>
                <Select
                  value={kitab.find(option => option.value === subjek)}
                  onChange={(selectedOption) => setSubjek(selectedOption.value)}
                  options={kitab}
                  className="custom-select"
                  styles={{
                    menu: (p) => ({ ...p, zIndex: 9999 }),
                    control: (p) => ({ ...p, height: "43px" }),
                  }}
                />
              </div>

              <div style={{ flex: "9", margin: "0 0 0 10px" }}>
                <input
                  type="text"
                  className="input-text"
                  value={tajuk}
                  onChange={(e) => setTajuk(e.target.value)}
                  placeholder="Tajuk (Nama Kitab)"
                  style={{ width: "100%" }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* ✅ repeat dropdown ONLY in add mode (as your current design) */}
      <div style={{ display: "flex" }}>
        {!isEditing && (
          <div className='ulang-container' style={{ flex: 1 }}>
            <Select
              value={repeatOptions.find(option => option.value === repeat)}
              onChange={(selectedOption) => setRepeat(selectedOption.value)}
              options={repeatOptions}
              classNamePrefix="custom-select"
              styles={{
                container: (provided) => ({ ...provided, width: '100%' }),
                control: (provided) => ({ ...provided, height: '43px' }),
                singleValue: (provided) => ({ ...provided, color: 'black' }),
                menu: (provided) => ({ ...provided, zIndex: 9999 }),
                option: (provided, state) => ({
                  ...provided,
                  color: state.isSelected ? 'white' : 'black',
                  backgroundColor: state.isSelected ? 'blue' : 'white',
                }),
                placeholder: (provided) => ({ ...provided, color: 'black' }),
              }}
            />
          </div>
        )}

        {repeat !== 'none' && (
          <div style={{ display: "flex", flex: "1", margin: "0 0 0 20px" }}>
            <label className="ulang-label" style={{ padding: "0 10px 0 0" }}>ulang<br />hingga</label>
            <input
              type="date"
              className="input-date"
              value={formatDate(repeatEndDate)}
              onChange={handleRepeatEndDateChange}
            />
          </div>
        )}
      </div>

      <div className="button-container">
        <button onClick={closeModal} className="btn-secondary" type="button">
          Kembali
        </button>

        {/* ✅ COPY only when editing */}
        {isEditing && (
          <button
            onClick={openCopyDialog}
            className="btn-secondary"
            type="button"
          >
            Copy
          </button>
        )}

        <button
          onClick={handleAddOrUpdateEvent}
          className="btn-primary"
          disabled={!isFormValid}
          type="button"
        >
          {isEditing ? 'Kemaskini' : 'Tambah'}
        </button>
      </div>

      {/* ✅ Copy dialog */}
      {copyOpen && (
        <div className="popevent-confirm-overlay">
          <div className="popevent-confirm-box">
            <h4 style={{ margin: 0 }}>Copy sebagai aktiviti baru</h4>

            <p style={{ marginTop: 8, marginBottom: 14, opacity: 0.9 }}>
              Pilih tarikh baru untuk salinan aktiviti ini:
            </p>

            <input
              type="date"
              className="input-date"
              value={copyDate}
              onChange={(e) => setCopyDate(e.target.value)}
            />

            <div className="popevent-confirm-actions" style={{ marginTop: 12 }}>
              <button
                type="button"
                className="popevent-confirm-btn"
                onClick={runCopyAsNew}
                disabled={!copyDate}
              >
                Teruskan
              </button>

              <button
                type="button"
                className="popevent-confirm-btn secondary"
                onClick={() => setCopyOpen(false)}
              >
                Batal
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AddEvent;
